import { customsInstructionSpecialNoteTitles, instructionStrings } from '@data/instruction';
import { commodity } from '@models/Commodity';
import { companyItem } from '@models/Company';
import {
  mapRemoteFieldToRemoteFileField,
  mapRemoteFileFieldToRemoteFile,
  remoteFile,
} from '@models/Files';
import { instructionStatusName, instructionStatus } from '@models/Instruction';
import { adminUser } from '@models/User';
import {
  createValidate,
  formStrings,
  mergeErrors,
  remoteFileField,
  requiredStringField,
  stringField,
  unlimitedStringField,
} from '@utils/forms';
import dayjs from 'dayjs';
import * as t from 'io-ts';
import * as h from 'tyrann-io';

export const insurance = h.taggedUnion([
  {
    tag: t.literal('INDIVIDUAL'),
    individual_insurance_files: t.array(remoteFile),
  },
  {
    tag: t.literal('BLANKET'),
    company: t.string,
    policy_no: t.string,
    start: t.string,
    end: t.string,
    blanket_insurance_files: t.array(remoteFile),
  },
  {
    tag: t.literal('NA'),
  },
]);

export type Insurance = t.TypeOf<typeof insurance>;

export const insuranceForm = h.taggedUnion([
  {
    tag: t.literal('INDIVIDUAL'),
    individual_insurance_files: h
      .array(remoteFileField(instructionStrings.additionalFiles))
      .withLabel(instructionStrings.additionalFiles),
  },
  {
    tag: t.literal('BLANKET'),
    company: requiredStringField(instructionStrings.insuranceCompany),
    policy_no: requiredStringField(instructionStrings.insurancePolicyNo),
    start: requiredStringField(instructionStrings.insurancePeriodStart),
    end: requiredStringField(instructionStrings.insurancePeriodEnd),
    blanket_insurance_files: h
      .array(remoteFileField(instructionStrings.additionalFiles))
      .withLabel(instructionStrings.additionalFiles),
  },
  {
    tag: t.literal('NA'),
  },
]);

export type InsuranceForm = t.TypeOf<typeof insuranceForm>;

export const insuranceFormLoose = h.taggedUnion([
  {
    tag: t.literal('INDIVIDUAL'),
    individual_insurance_files: h
      .array(remoteFileField(instructionStrings.additionalFiles))
      .withLabel(instructionStrings.additionalFiles),
  },
  {
    tag: t.literal('BLANKET'),
    company: stringField(instructionStrings.insuranceCompany),
    policy_no: stringField(instructionStrings.insurancePolicyNo),
    start: stringField(instructionStrings.insurancePeriodStart),
    end: stringField(instructionStrings.insurancePeriodEnd),
    blanket_insurance_files: t.array(remoteFileField(instructionStrings.additionalFiles)),
  },
  {
    tag: t.literal('NA'),
  },
]);

export const customsInstructionSpecialNoteItem = t.type({
  title: requiredStringField(instructionStrings.specialNoteNote),
  selected: t.boolean,
  note: unlimitedStringField(instructionStrings.specialNoteNote),
});

export type CustomsInstructionSpecialNoteItem = t.TypeOf<typeof customsInstructionSpecialNoteItem>;

export const customsInstructionBase = t.type({
  custom_files: t.array(remoteFile),
  commodities: t.array(commodity),
  rcep_file: h.omittable(remoteFile),
  insurance: h.omittable(insurance),
  has_special_note: t.boolean,
  special_note: t.array(customsInstructionSpecialNoteItem),
});

export const customsInstruction = t.type({
  ...customsInstructionBase.props,
  writer: h.omittable(adminUser),
  writer_company: h.omittable(companyItem),
  writer_id: h.omittable(t.number),
  deadline: t.string,
  status: t.array(instructionStatus),
  use_rcep_changed: h.omittable(t.boolean),
  created_at: t.string,
  updated_at: t.string,
});

export type CustomsInstruction = t.TypeOf<typeof customsInstruction>;

export const customsInstructionListItem = t.type({
  d2d_no: t.string,
  deadline: t.string,
  status: t.array(instructionStatus),
  eta: h.omittable(t.string),
  etd: h.omittable(t.string),
  created_at: t.string,
  updated_at: t.string,
});

export type CustomsInstructionListItem = t.TypeOf<typeof customsInstructionListItem>;

export const customsInstructionList = t.type({
  results: h.array(customsInstructionListItem),
  count: t.number,
});

export const customsInstructionForm = t.type({
  custom_files: h
    .array(remoteFileField(instructionStrings.customsFiles))
    .min(1, `${instructionStrings.customsFiles}をアップロードしてください`)
    .withLabel(instructionStrings.customsFiles),
  commodities: h
    .array(t.number)
    .min(1, `${instructionStrings.commodities}は必須です。`)
    .withLabel(instructionStrings.commodities), // Id of commodities
  rcep_file: h.omittable(remoteFileField(instructionStrings.rcepFile)),
  insurance: insuranceForm,
  old_blanket_insurance: h.omittable(insuranceFormLoose),
  old_individual_insurance: h.omittable(insuranceFormLoose),
  has_special_note: customsInstructionBase.props.has_special_note,
  special_note: customsInstructionBase.props.special_note,
  special_note_titles: h.array(t.string),
});

export type CustomsInstructionForm = t.TypeOf<typeof customsInstructionForm>;

export const customsInstructionFormLoose = t.type({
  custom_files: h.array(remoteFileField(instructionStrings.customsFiles)),
  commodities: h.array(t.number),
  rcep_file: h.omittable(remoteFileField(instructionStrings.rcepFile)),
  insurance: insuranceFormLoose,
});

export type CustomsInstructionFormLoose = t.TypeOf<typeof customsInstructionFormLoose>;

export const patchCustomsInstruction = t.partial({
  ...customsInstructionBase.props,
  commodities: t.array(t.number),
  status_name: instructionStatusName,
});

export type PatchCustomsInstruction = t.TypeOf<typeof patchCustomsInstruction>;

export const adminPatchCustomsInstruction = t.partial({
  ...customsInstructionBase.props,
  commodities: t.array(t.number),
  message: t.string,
  status_name: instructionStatusName,
});

export type AdminPatchCustomsInstruction = t.TypeOf<typeof adminPatchCustomsInstruction>;

export const validateCustomsInstructionForm = (values: CustomsInstructionForm) => {
  const e = {
    ...(values.insurance.tag === 'BLANKET' &&
      values.insurance.start &&
      values.insurance.end && {
        ...(values.insurance.start > values.insurance.end && {
          insurance: {
            end: instructionStrings.insuranceBlanketStartGtEnd,
          },
        }),
        ...(values.insurance.end < dayjs().format('YYYY-MM-DD') && {
          insurance: {
            end: instructionStrings.insuranceBlanketExpired,
          },
        }),
      }),
    ...createValidate(customsInstructionForm)(values),
  };

  const specialNoteErrors = validateCustomsInstructionSpecialNote(values);

  return mergeErrors(e, specialNoteErrors);
};

export const validateCustomsInstructionSpecialNote = (
  values: Pick<CustomsInstructionForm, 'special_note' | 'special_note_titles' | 'has_special_note'>,
) => {
  const e: Record<string, any> = {};

  if (values.has_special_note) {
    if (values.special_note_titles.length === 0) {
      e.special_note_titles ||= formStrings.notEmpty(instructionStrings.specialNoteDetails);
    }

    for (const special_note_title of values.special_note_titles) {
      const special_note_index = values.special_note.findIndex(
        (r) => r.title === special_note_title,
      );
      if (special_note_index === -1) {
        throw new Error(`Internal error: ${special_note_title} not found in values.special_note`);
      }
    }
  }

  return e;
};

export const validateCustomsInstructionFormLoose = (values: CustomsInstructionForm) => {
  return createValidate(customsInstructionFormLoose)(values);
};

export const validateRcepForm = (values: CustomsInstructionForm) => {
  const e = {
    ...((!values.rcep_file?.file || !values.rcep_file?.remote) && {
      rcep_file: formStrings.notEmpty(instructionStrings.rcepFile),
    }),
  };
  return e;
};

export const mapCustomsInstructionToCustomsInstructionForm = (
  d: CustomsInstruction,
): CustomsInstructionForm => ({
  custom_files: d.custom_files.map(mapRemoteFieldToRemoteFileField),
  commodities: d.commodities.map((c) => c.id),
  rcep_file: d.rcep_file ? mapRemoteFieldToRemoteFileField(d.rcep_file) : null,
  insurance: mapInsuranceToInsuranceForm(d.insurance ?? { tag: 'NA' }),
  old_blanket_insurance: undefined,
  old_individual_insurance: undefined,
  has_special_note: d.has_special_note,
  special_note: d.special_note,
  special_note_titles: d.special_note.filter((s) => s.note.trim() !== '').map((s) => s.title),
});

export const mapCustomsInstructionFormToPatchCustomsInstruction = (
  d: CustomsInstructionForm,
): PatchCustomsInstruction => ({
  ...d,
  custom_files: d.custom_files.map(mapRemoteFileFieldToRemoteFile),
  rcep_file: d.rcep_file ? mapRemoteFileFieldToRemoteFile(d.rcep_file) : null,
  insurance: mapInsuranceFormToInsurance(d.insurance),
});

export const mapInsuranceToInsuranceForm = (d: Insurance): InsuranceForm => {
  switch (d.tag) {
    case 'INDIVIDUAL':
      return {
        ...d,
        individual_insurance_files: d.individual_insurance_files.map(
          mapRemoteFieldToRemoteFileField,
        ),
      };

    case 'BLANKET':
      return {
        ...d,
        blanket_insurance_files: d.blanket_insurance_files.map(mapRemoteFieldToRemoteFileField),
      };
    case 'NA':
      return d;
  }
};

export const mapInsuranceFormToInsurance = (d: InsuranceForm): Insurance => {
  switch (d.tag) {
    case 'INDIVIDUAL':
      return {
        ...d,
        individual_insurance_files: d.individual_insurance_files.map(
          mapRemoteFileFieldToRemoteFile,
        ),
      };

    case 'BLANKET':
      return {
        ...d,
        blanket_insurance_files: d.blanket_insurance_files.map(mapRemoteFileFieldToRemoteFile),
      };
    case 'NA':
      return d;
  }
};

export const getInsuranceFormInitialValues = (tag: InsuranceForm['tag']): InsuranceForm => {
  switch (tag) {
    case 'INDIVIDUAL':
      return {
        tag: 'INDIVIDUAL',
        individual_insurance_files: [],
      };
    case 'BLANKET':
      return {
        tag: 'BLANKET',
        company: '',
        policy_no: '',
        start: '',
        end: '',
        blanket_insurance_files: [],
      };
    case 'NA':
      return {
        tag: 'NA',
      };
  }
};

export const getCustomsErrorLabels = (errors: any) =>
  [
    'commodities' in errors && customsInstructionForm.props.commodities.label,
    'custom_files' in errors && customsInstructionForm.props.custom_files.label,
    'rcep_file' in errors && instructionStrings.rcepUsage,
    'insurance' in errors && 'company' in errors.insurance && instructionStrings.insuranceCompany,
    'insurance' in errors &&
      'policy_no' in errors.insurance &&
      instructionStrings.insurancePolicyNo,
    'insurance' in errors &&
      ('start' in errors.insurance || 'end' in errors.insurance) &&
      instructionStrings.insurancePeriod,
    'has_special_note' in errors && instructionStrings.specialNote,
    ('special_note' in errors || 'special_note_titles' in errors) &&
      instructionStrings.specialNoteDetails,
  ].filter((s): s is string => Boolean(s));

export const customsInstructionListQueryParams = t.partial({
  page: h.number().cast(),
  d2d_no: t.string,
  status: t.array(t.string),
});

export type CustomsInstructionListQueryParams = t.TypeOf<typeof customsInstructionListQueryParams>;

export const customsInstructionListSearchType = t.partial({
  ...customsInstructionListQueryParams.props,
  status: t.string,
});

export const customsInstructionFormInitialValues: CustomsInstructionForm = {
  custom_files: [],
  commodities: [],
  rcep_file: null,
  insurance: {
    tag: 'NA',
  },
  old_blanket_insurance: undefined,
  old_individual_insurance: undefined,
  has_special_note: false,
  special_note: customsInstructionSpecialNoteTitles.map((title) => ({
    title,
    selected: false,
    note: '',
  })),
  special_note_titles: [],
};
